import { useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { X, MagicWand, CheckCircle, ArrowRight, WarningCircle } from "@phosphor-icons/react";

const SAMPLE = `ST001 5028 N 12th St
RH006 · 6220 Carpenter Street Unit 2
PC013 5028 12th Street
FLOAT-1 → 28 E Seymour
DK001 4820 Wingohocking`;

export default function BulkRingSyncModal({ open, onClose, onApplied }) {
  const [text, setText] = useState("");
  const [diffs, setDiffs] = useState(null);
  const [checked, setChecked] = useState(new Set());
  const [loading, setLoading] = useState(false);
  const [applying, setApplying] = useState(false);

  const runPreview = async () => {
    if (!text.trim()) return toast.error("Paste your Ring list first");
    setLoading(true);
    try {
      const r = await api.post("/security-fleet/bulk-ring-sync/preview", { text });
      setDiffs(r.data.diffs || []);
      // Pre-select every "update" AND "create" row — the two actionable ones.
      const s = new Set();
      (r.data.diffs || []).forEach((d, i) => {
        if (d.action === "update" || d.action === "create") s.add(i);
      });
      setChecked(s);
      const diffs_ = r.data.diffs || [];
      const nUpdate = diffs_.filter(d => d.action === "update").length;
      const nCreate = diffs_.filter(d => d.action === "create").length;
      toast.success(`Parsed ${r.data.parsed_count} · ${nUpdate} update · ${nCreate} new`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Parse failed");
    } finally { setLoading(false); }
  };

  const apply = async () => {
    if (!diffs) return;
    const changes = [];
    diffs.forEach((d, i) => {
      if (!checked.has(i)) return;
      if (d.action === "create") {
        changes.push({
          action: "create",
          new_system_code: d.parsed_code,
          new_address: d.parsed_address,
          new_unit: d.parsed_unit,
          target_property_id: d.target_property_id || null,
        });
      } else if (d.asset_id) {
        changes.push({
          action: "update",
          asset_id: d.asset_id,
          new_system_code: d.parsed_code,
          new_address: d.parsed_address,
          new_unit: d.parsed_unit,
        });
      }
    });
    if (changes.length === 0) return toast.error("Nothing selected to apply");
    setApplying(true);
    try {
      const r = await api.post("/security-fleet/bulk-ring-sync/apply", { changes });
      const nUp = r.data.updated || 0;
      const nNew = r.data.created || 0;
      const bits = [];
      if (nUp) bits.push(`${nUp} updated`);
      if (nNew) bits.push(`${nNew} new`);
      toast.success(bits.length ? bits.join(" · ") : "No changes applied");
      onApplied?.();
      onClose();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Apply failed");
    } finally { setApplying(false); }
  };

  // Inline edit of any row field. Also auto-promotes "no_match" rows to
  // "create" once the user types a code or address (so Apply picks them up).
  const updateDiff = (i, field, value) => {
    setDiffs(prev => {
      const next = prev.slice();
      const row = { ...next[i], [field]: value };
      if (row.action !== "update" && row.action !== "no_change") {
        const hasAny = (row.parsed_code || "").trim() || (row.parsed_address || "").trim();
        row.action = hasAny ? "create" : "no_match";
      }
      next[i] = row;
      return next;
    });
  };

  // "+ Add manually" — append a blank editable row the user can type into.
  const addManualRow = () => {
    setDiffs(prev => {
      const list = prev || [];
      const next = list.slice();
      next.push({
        parsed_code: "", parsed_address: "", parsed_unit: "",
        current_code: "", current_address: "", current_unit: "",
        asset_id: null, target_property_id: null,
        target_property_address: "",
        action: "no_match",
        _manual: true,
      });
      setChecked(c => new Set([...c, next.length - 1]));
      return next;
    });
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 bg-black/60 z-50 flex items-start justify-center p-4 overflow-y-auto"
      data-testid="bulk-ring-modal" onClick={onClose}>
      <div className="bg-white border-4 border-[#0f172a] max-w-3xl w-full my-8 shadow-[8px_8px_0_#000]"
        onClick={e => e.stopPropagation()}>
        <div className="p-4 border-b-2 border-slate-900 flex items-center justify-between bg-fuchsia-100">
          <div>
            <div className="text-[10px] font-black uppercase tracking-widest text-fuchsia-800">Bulk Ring Sync</div>
            <div className="font-display font-black text-xl">Paste your Ring list once</div>
          </div>
          <button onClick={onClose} className="w-10 h-10 border-2 border-slate-900 bg-white hover:bg-red-100 flex items-center justify-center">
            <X weight="bold" size={18}/>
          </button>
        </div>
        <div className="p-4 space-y-3">
          <textarea rows={8} value={text} onChange={e => setText(e.target.value)}
            placeholder={SAMPLE}
            data-testid="bulk-ring-input"
            className="w-full p-2 border-2 border-slate-300 font-mono text-xs"/>
          <div className="flex items-center gap-2 flex-wrap">
            <button onClick={runPreview} disabled={loading}
              data-testid="bulk-ring-preview-btn"
              className="h-10 px-4 border-2 border-[#0f172a] bg-fuchsia-500 hover:bg-fuchsia-400 text-white font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1 shadow-[2px_2px_0_#0f172a]">
              <MagicWand weight="fill" size={14}/> {loading ? "PARSING…" : "PARSE + MATCH"}
            </button>
            {diffs && diffs.length > 0 && (
              <>
                <span className="text-xs text-slate-500">
                  {checked.size} of {diffs.length} rows selected
                </span>
                <button onClick={apply} disabled={applying || checked.size === 0}
                  data-testid="bulk-ring-apply-btn"
                  className="ml-auto h-10 px-4 border-2 border-emerald-700 bg-emerald-600 hover:bg-emerald-500 text-white font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1">
                  <CheckCircle weight="fill" size={14}/> {applying ? "APPLYING…" : `APPLY ${checked.size}`}
                </button>
              </>
            )}
          </div>
          {diffs && diffs.length === 0 && (
            <div className="p-3 border-2 border-amber-400 bg-amber-50 text-xs italic text-amber-800">
              <WarningCircle size={14} className="inline mr-1"/> AI didn't find any rows. Check formatting?
            </div>
          )}
          {diffs && diffs.length > 0 && (
            <div className="border-2 border-slate-300 max-h-96 overflow-y-auto">
              {diffs.map((d, i) => (
                <div key={i} className={`flex items-start gap-2 p-2 border-b border-slate-100 hover:bg-slate-50 ${d.action === "no_match" ? "bg-red-50/40" : ""}`}>
                  <input type="checkbox"
                    data-testid={`bulk-ring-row-check-${i}`}
                    disabled={d.action === "no_change"}
                    checked={checked.has(i)}
                    onChange={() => {
                      const s = new Set(checked);
                      s.has(i) ? s.delete(i) : s.add(i);
                      setChecked(s);
                    }}
                    className="mt-2"/>
                  <div className="flex-1 min-w-0 text-xs space-y-1">
                    <div className="flex items-center gap-2 flex-wrap">
                      {d.action === "update" && d.current_code !== d.parsed_code && (
                        <><span className="text-slate-400 line-through font-mono">{d.current_code || "—"}</span> <ArrowRight size={10} className="text-cyan-600"/></>
                      )}
                      <input type="text"
                        data-testid={`bulk-ring-row-code-${i}`}
                        value={d.parsed_code || ""}
                        onChange={(e) => updateDiff(i, "parsed_code", e.target.value)}
                        placeholder="CODE"
                        className="font-mono font-black text-slate-900 bg-white border border-slate-300 rounded px-1.5 py-0.5 w-28 focus:outline-none focus:border-fuchsia-500"/>
                      {d.action === "no_change" && <span className="text-[9px] font-black uppercase tracking-widest bg-slate-200 text-slate-600 px-1">no change</span>}
                      {d.action === "no_match" && <span className="text-[9px] font-black uppercase tracking-widest bg-red-200 text-red-800 px-1">no match</span>}
                      {d.action === "update" && <span className="text-[9px] font-black uppercase tracking-widest bg-cyan-200 text-cyan-800 px-1">update</span>}
                      {d.action === "create" && <span className="text-[9px] font-black uppercase tracking-widest bg-emerald-200 text-emerald-800 px-1">+ NEW</span>}
                      {d._manual && <span className="text-[9px] font-black uppercase tracking-widest bg-fuchsia-100 text-fuchsia-700 px-1">manual</span>}
                    </div>
                    <div className="flex items-center gap-2 flex-wrap">
                      {d.action === "update" && d.current_address !== d.parsed_address && (
                        <><span className="text-slate-400 line-through truncate max-w-[160px]">{d.current_address || "—"}</span> <ArrowRight size={10} className="text-cyan-600"/></>
                      )}
                      <input type="text"
                        data-testid={`bulk-ring-row-addr-${i}`}
                        value={d.parsed_address || ""}
                        onChange={(e) => updateDiff(i, "parsed_address", e.target.value)}
                        placeholder="Street address (e.g. 5028 N 12th St)"
                        className="text-slate-800 font-bold bg-white border border-slate-300 rounded px-1.5 py-0.5 flex-1 min-w-[160px] focus:outline-none focus:border-fuchsia-500"/>
                      <input type="text"
                        data-testid={`bulk-ring-row-unit-${i}`}
                        value={d.parsed_unit || ""}
                        onChange={(e) => updateDiff(i, "parsed_unit", e.target.value)}
                        placeholder="Unit"
                        className="text-slate-500 bg-white border border-slate-300 rounded px-1.5 py-0.5 w-16 focus:outline-none focus:border-fuchsia-500"/>
                    </div>
                    {d.action === "create" && d.target_property_address && (
                      <div className="text-[10px] text-emerald-700">
                        matched property: {d.target_property_address}
                      </div>
                    )}
                    {d.action === "no_match" && !d._manual && (
                      <div className="text-[10px] text-red-700/80 italic">
                        Fix the address or code to turn this into a + NEW
                      </div>
                    )}
                  </div>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-center justify-between pt-1">
            <button
              onClick={addManualRow}
              data-testid="bulk-ring-add-manual-btn"
              className="h-9 px-3 border-2 border-slate-900 bg-white hover:bg-fuchsia-50 text-slate-900 font-black uppercase text-[11px] tracking-widest inline-flex items-center gap-1 shadow-[2px_2px_0_#0f172a]">
              + Add manually
            </button>
            {diffs && diffs.length > 0 && (
              <span className="text-[10px] text-slate-500 italic">
                Tip: edit any field inline — a `no match` becomes `+ NEW` as you type
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
